import { useEffect, useState } from 'react';
import api, { SodPolicy } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg, splitList } from '../utils';

const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

export default function SodPoliciesPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('iga-admin');
  const [policies, setPolicies] = useState<SodPolicy[]>([]);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [conflicting, setConflicting] = useState('');
  const [severity, setSeverity] = useState<(typeof SEVERITIES)[number]>('high');

  const load = async () => {
    setError('');
    try {
      const r = await api.get<SodPolicy[]>('/sod/policies');
      setPolicies(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async () => {
    setError('');
    try {
      await api.post('/sod/policies', { name, conflicting: splitList(conflicting), severity });
      setName('');
      setConflicting('');
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const toggle = async (p: SodPolicy) => {
    try {
      await api.patch(`/sod/policies/${p.id}`, { enabled: !p.enabled });
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/sod/policies/${id}`);
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Segregation-of-Duties — Policies</h2>
      {error && <p className="text-coral">{error}</p>}

      {isAdmin && (
        <section className="card space-y-3">
          <h3 className="font-medium text-coral">Neue Policy</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="label">Konfliktrollen (≥ 2, Komma-getrennt)</label>
              <input
                className="input"
                value={conflicting}
                onChange={(e) => setConflicting(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Schweregrad</label>
              <select
                className="input"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as (typeof SEVERITIES)[number])}
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            className="btn-primary"
            disabled={!name || splitList(conflicting).length < 2}
            onClick={create}
          >
            Anlegen
          </button>
        </section>
      )}

      <section className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-edge">
              <th className="th">Name</th>
              <th className="th">Konfliktrollen</th>
              <th className="th">Schwere</th>
              <th className="th">Aktiv</th>
              <th className="th"></th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-b border-edge/50">
                <td className="td">{p.name}</td>
                <td className="td">{p.conflicting.join(', ')}</td>
                <td className="td">{p.severity}</td>
                <td className="td">{p.enabled ? 'ja' : 'nein'}</td>
                <td className="td">
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button className="btn-ghost" onClick={() => toggle(p)}>
                        {p.enabled ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                      <button className="btn-ghost" onClick={() => remove(p.id)}>
                        Löschen
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr>
                <td className="td text-gray-500" colSpan={5}>
                  Keine Policies.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
